/**
 * OutcomeControl — "what happened with this person?" for someone on an
 * outreach list (docs/crm-outreach-lists-plan.md §B6). ONE control, mounted
 * wherever the recruiter might be when they know the answer: the Next-up bar,
 * Call Mode's header (top of Next steps on a phone), and the lead page's
 * outreach chip.
 *
 * A split button, the same width on every screen: the main half logs the
 * commonest answer in one click (No answer — or Sent a text / No answer when
 * the item says that is what just happened), and "Log outcome" opens the rest.
 * Three of the answers need one more fact before they can be written, and ask
 * for it inside the same menu rather than in a modal on top of a live call:
 *   - Not interested → why (the recruiter reasons; the system-only codes are
 *     never offered);
 *   - Call back at… → when, as a REAL booked callback (it shows on Today, the
 *     calendar and the reminders). A time outside business hours comes back
 *     422 and is only booked after an explicit "Book it anyway";
 *   - Try later… → when to put them back on the list.
 * "Asked us not to contact them" confirms first — it opts them out of texts,
 * which is not something to do on a mis-click.
 *
 * The outcome is written FIRST; only a write that landed moves anything on.
 * Then: the confirmation line (built from the server's answer, so it never
 * claims a stage change that did not happen), onLogged, and — inside a running
 * Next-up session — afterOutcome, which records the person as worked and
 * serves the next one. The control stays busy until all of that has finished:
 * a list session re-fetches the list before it moves on, and for that second
 * or two the bar still shows the same person — an enabled button there would
 * log a second try against them. The one refusal that also moves a session on
 * is `member_not_workable`: the server has already finished that person, so
 * staying on them would only earn the same refusal from every button.
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ArrowLeft, CalendarClock, Check, ChevronDown, Clock, Loader2, PhoneOff } from 'lucide-react';
import { Z, useConfirm } from '../components/ui';
import { dispositionReasonOptions, notifyStatsChanged } from '../lib';
import { useWorkQueue } from '../WorkQueueContext';
import {
  callbackPicks,
  callingHoursBlock,
  outcomeConfirmation,
  pickedTimeProblem,
  retryPicks,
} from '../workQueueLogic';
import { OUTCOME_MENU } from './outreachLogic';
import { logOutreachOutcome, outreachErrorMessage } from './api';
import type { OutreachLoggableOutcome, OutreachOutcomeRequest, OutreachOutcomeResponse } from './types';

export interface OutcomeControlProps {
  listId: string;
  memberId: string;
  contactId: string;
  /** Where it is mounted — sets its size and whether logging moves a session on. */
  surface: 'bar' | 'callmode' | 'lead';
  /** Offer this as the main button (only the one-click answers qualify). */
  preselect?: OutreachLoggableOutcome | null;
  /**
   * Runs once the outcome is saved. May return a promise (Call Mode saves the
   * Notes draft before it closes); the control stays busy until it settles.
   */
  onLogged?: (res: OutreachOutcomeResponse) => void | Promise<void>;
  /**
   * Runs when the server refuses because it has already finished this person on
   * the list (code `member_not_workable`). Nothing was logged, so onLogged does not
   * run — but whatever is showing the membership (the lead-page chip) is stale and
   * should re-read it.
   */
  onStale?: () => void;
  /**
   * Inside a running Next-up session, go to the next person once the outcome
   * lands. Defaults to true, except in Call Mode, where logging closes the
   * workspace and "Log & next" is the recruiter's explicit choice.
   */
  goToNext?: boolean;
  disabled?: boolean;
  /**
   * Host the confirmation / error line yourself (the bar stacks it with its
   * other notices). Omitted = the control shows it under itself.
   */
  onFlash?: (tone: 'ok' | 'error', text: string) => void;
}

/** The answers that need nothing more than a click — the only ones the main button may carry. */
const ONE_CLICK: ReadonlySet<OutreachLoggableOutcome> = new Set(['no_answer', 'voicemail', 'texted']);

type View = 'main' | 'not_interested' | 'call_back' | 'try_later';

/** How long the confirmation line stays up. Long enough to read on the way to the next call. */
const FLASH_MS = 6_000;

/** Local-time value for an `<input type="datetime-local">` (its `min`). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Tue, Sep 29, 10:00 AM" — the picks' secondary line. */
function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const labelOf = (o: OutreachLoggableOutcome) => OUTCOME_MENU.find((m) => m.value === o)?.label ?? 'No answer';

const SIZE = {
  bar: {
    main: 'min-h-[44px] w-[7rem] rounded-l-full pl-3 pr-2 text-xs sm:min-h-0 sm:py-1.5',
    more: 'min-h-[44px] rounded-r-full px-2 text-xs sm:min-h-0 sm:py-1.5',
  },
  compact: {
    main: 'min-h-[44px] w-[7.75rem] rounded-l-lg px-3 text-sm sm:min-h-0 sm:py-1.5',
    more: 'min-h-[44px] rounded-r-lg px-2 text-sm sm:min-h-0 sm:py-1.5',
  },
  // The lead page's chip: the compact shape from sm up, but on a phone the
  // control sits on its own line under the "On outreach list" sentence, so
  // the main half stretches to fill it (min-w-0: it may also give way and
  // truncate on the narrowest phones) and the menu half keeps its words on
  // one line.
  lead: {
    main: 'min-h-[44px] w-[7.75rem] rounded-l-lg px-3 text-sm max-sm:min-w-0 max-sm:w-auto max-sm:flex-1 sm:min-h-0 sm:py-1.5',
    more: 'min-h-[44px] rounded-r-lg px-2 text-sm max-sm:flex-shrink-0 max-sm:whitespace-nowrap max-sm:px-3 sm:min-h-0 sm:py-1.5',
  },
} as const;

const menuItemCls =
  'flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus-visible:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-primary-900/20 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:py-1.5';

const fieldCls =
  'min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0';

export default function OutcomeControl({
  listId,
  memberId,
  contactId,
  surface,
  preselect,
  onLogged,
  onStale,
  goToNext,
  disabled = false,
  onFlash,
}: OutcomeControlProps) {
  const queue = useWorkQueue();
  const confirm = useConfirm();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('main');
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState<OutreachLoggableOutcome | null>(null);
  // The same flag, read synchronously: two clicks inside one render would
  // both still see `busy` as null.
  const busyRef = useRef(false);
  const [flash, setFlash] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const primary: OutreachLoggableOutcome = preselect && ONE_CLICK.has(preselect) ? preselect : 'no_answer';
  const size = surface === 'bar' ? SIZE.bar : surface === 'lead' ? SIZE.lead : SIZE.compact;
  // On the lead page the whole control is full-width below sm (see SIZE.lead);
  // the other two surfaces keep their fixed widths at every size.
  const fullOnPhone = surface === 'lead' ? 'max-sm:w-full' : '';
  const navigateNext = goToNext ?? surface !== 'callmode';
  // Which edge the menu and the confirmation line hang from. They grow away
  // from that edge, so it must be the one with room beyond it. In Call Mode
  // below lg the control is the FIRST thing in the Next-steps block, near the
  // left of the screen — hung from its right edge, the menu would open mostly
  // off-screen, clipped by the scrolling pane. From lg up it sits at the right
  // of the header, where the right edge is the one with room. (The two mounts
  // switch at exactly lg: the phone block is lg:hidden, the header copy is
  // hidden lg:block.) The bar and the lead chip sit at the right, as before.
  const edge = surface === 'callmode' ? 'left-0 lg:left-auto lg:right-0' : 'right-0';

  // A new person on deck: close the menu and forget the half-picked time. The
  // confirmation line is deliberately KEPT — in the bar it is the only place
  // the recruiter sees that the last outcome landed, and the bar has already
  // moved on to the next person by the time they read it.
  useEffect(() => {
    setOpen(false);
    setView('main');
    setPicked('');
  }, [memberId]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = window.setTimeout(() => setFlash(null), flash.tone === 'error' ? FLASH_MS + 4_000 : FLASH_MS);
    return () => window.clearTimeout(t);
  }, [flash]);

  const close = useCallback(() => {
    setOpen(false);
    setView('main');
    setPicked('');
  }, []);

  const show = useCallback((tone: 'ok' | 'error', text: string) => {
    if (onFlash) onFlash(tone, text);
    else setFlash({ tone, text });
  }, [onFlash]);

  // Outside click closes (mousedown, so a click that lands on another control
  // still does its own thing).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Land keyboard focus in the menu when it opens or changes view.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[data-autofocus], [role="menuitem"]');
    first?.focus();
  }, [open, view]);

  /** Write the outcome; on success, tell everyone who needs to know. */
  const log = useCallback(async (outcome: OutreachLoggableOutcome, extra: Partial<OutreachOutcomeRequest> = {}) => {
    if (busyRef.current || disabled) return;
    busyRef.current = true;
    setBusy(outcome);
    setFlash(null);
    const release = () => {
      busyRef.current = false;
      setBusy(null);
    };
    // Stamped only when the recruiter clicked through "calling now needs a
    // reason" this sitting AND it is in fact outside 8am–9pm right now.
    const outsideHours = !!callingHoursBlock(new Date()) && queue.outsideHoursAck;
    const body: OutreachOutcomeRequest = { outcome, ...extra, ...(outsideHours ? { outside_hours: true } : {}) };
    let res: OutreachOutcomeResponse;
    try {
      res = await logOutreachOutcome(listId, memberId, body);
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const cb = extra.callback;
      // The booking path answers an outside-hours time with a 422 and the
      // hours in words; it is only booked after an explicit "anyway".
      const outsideHoursRefusal = outcome === 'call_back' && status === 422
        && (data?.code === 'outside_hours' || !data?.code)
        && !!cb && !cb.allow_outside_hours;
      if (outsideHoursRefusal && cb) {
        release();
        const ok = await confirm({
          title: 'That time is outside calling hours',
          message: `${outreachErrorMessage(e, 'That time is outside business hours.')} Only book it if they asked for that time.`,
          confirmLabel: 'Book it anyway',
        });
        if (ok) await log(outcome, { ...extra, callback: { ...cb, allow_outside_hours: true } });
        return;
      }
      // The menu closes on a failure too: the line that says so only shows
      // under a closed menu, and a failed pick from an open one (a reason,
      // Interested, a call-back time) would otherwise fail without a word.
      close();
      // The server has already finished this person on the list (someone else
      // logged them, they texted STOP, the list was closed). Every button
      // would get the same refusal, so a running session moves past them —
      // staying busy until it has, like a save. "Asked us not to contact
      // them" and "Wrong number" are still recorded on the PERSON then
      // (lead_effect says what landed), so that sentence is not an error.
      if (data?.code === 'member_not_workable') {
        show(data?.lead_effect ? 'ok' : 'error', outreachErrorMessage(e, 'This person is already finished on this list.'));
        notifyStatsChanged();
        onStale?.();
        try {
          if (queue.active) await queue.afterOutcome(contactId, { navigate: navigateNext });
        } catch (err: any) {
          console.error('[outreach] moving the session on failed:', err?.message);
        } finally {
          release();
        }
        return;
      }
      show('error', outreachErrorMessage(e, "That wasn't saved. Please try again."));
      release();
      return;
    }

    // Saved. Everything from here on is follow-up, and the control stays busy
    // through all of it (see the header): released as soon as the write
    // landed, it would sit enabled on the SAME person while the list session
    // re-fetches, and a second click would be written as a second try — the
    // server's attempts check only catches two requests in flight together,
    // not one after the other. Nothing below may report the save as failed.
    close();
    show('ok', outcomeConfirmation(outcome, res));
    notifyStatsChanged(); // the sidebar badge and Today's list counts moved
    try {
      await onLogged?.(res);
    } catch (e: any) {
      console.error('[outreach] after-outcome step failed:', e?.message);
    }
    try {
      if (queue.active) await queue.afterOutcome(contactId, { navigate: navigateNext });
    } catch (e: any) {
      console.error('[outreach] moving the session on failed:', e?.message);
    } finally {
      // A no-op when Call Mode closed (and unmounted this) through onLogged.
      release();
    }
  }, [disabled, queue, listId, memberId, contactId, close, show, onLogged, onStale, navigateNext, confirm]);

  /** A menu row was chosen: log it, or open the step that asks for the missing fact. */
  const choose = useCallback(async (outcome: OutreachLoggableOutcome) => {
    if (outcome === 'not_interested' || outcome === 'call_back' || outcome === 'try_later') {
      setView(outcome);
      setPicked('');
      return;
    }
    if (outcome === 'asked_to_stop') {
      close();
      const ok = await confirm({
        title: 'They asked us not to contact them?',
        message: 'They will be opted out of texts, marked "Do not contact", and moved to Unreachable. Nobody will be able to text them from the CRM after this.',
        confirmLabel: 'Yes, they asked us to stop',
        danger: true,
      });
      if (ok) await log('asked_to_stop');
      return;
    }
    await log(outcome);
  }, [close, confirm, log]);

  // Keyboard: Escape backs out one step (then closes); 1–9 pick a row on the
  // main view; arrows move between rows.
  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (view !== 'main') setView('main');
      else close();
      return;
    }
    const target = e.target as HTMLElement;
    const typing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    if (view === 'main' && !typing && /^[1-9]$/.test(e.key)) {
      const row = OUTCOME_MENU[Number(e.key) - 1];
      if (row) {
        e.preventDefault();
        void choose(row.value);
      }
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !typing) {
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
      if (!items.length) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  };

  const pickProblem = view === 'call_back' || view === 'try_later'
    ? (picked ? pickedTimeProblem(picked, view) : null)
    : null;

  const logPicked = (iso: string) => {
    if (view === 'call_back') void log('call_back', { callback: { slot_start: iso } });
    else if (view === 'try_later') void log('try_later', { retry_at: iso });
  };

  const backRow = (title: string) => (
    <div className="flex items-center gap-1.5 px-1 pb-1">
      <button
        type="button"
        onClick={() => setView('main')}
        aria-label="Back to all outcomes"
        className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-7 sm:w-7"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{title}</span>
    </div>
  );

  const timeStep = (kind: 'call_back' | 'try_later') => {
    const picks = kind === 'call_back' ? callbackPicks() : retryPicks();
    return (
      <>
        {backRow(kind === 'call_back' ? 'Call them back at…' : 'Try them again…')}
        {kind === 'call_back' && (
          <p className="px-2.5 pb-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            Books a real call — it shows on Today, the calendar and the reminders.
          </p>
        )}
        {picks.map((p) => (
          <button
            key={p.label}
            type="button"
            role="menuitem"
            onClick={() => logPicked(p.iso)}
            disabled={!!busy}
            className={menuItemCls}
          >
            {kind === 'call_back'
              ? <CalendarClock className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              : <Clock className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />}
            {p.label}
            <span className="ml-auto flex-shrink-0 text-[11px] font-normal text-gray-400 dark:text-gray-500">{whenLabel(p.iso)}</span>
          </button>
        ))}
        <div className="mt-1 border-t border-gray-100 px-2.5 pb-1 pt-2 dark:border-gray-700">
          <label htmlFor={`${menuId}-when`} className="block text-[11px] font-medium text-gray-500 dark:text-gray-400">
            Pick a day and time
          </label>
          <input
            id={`${menuId}-when`}
            type="datetime-local"
            value={picked}
            min={toLocalInput(new Date())}
            onChange={(e) => setPicked(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && picked && !pickedTimeProblem(picked, kind)) {
                e.preventDefault();
                logPicked(new Date(picked).toISOString());
              }
            }}
            className={`mt-1 ${fieldCls}`}
          />
          {pickProblem && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{pickProblem}</p>}
          <button
            type="button"
            onClick={() => logPicked(new Date(picked).toISOString())}
            disabled={!picked || !!pickProblem || !!busy}
            className="mt-1.5 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy === kind ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {kind === 'call_back' ? 'Book the call' : 'Try them then'}
          </button>
        </div>
      </>
    );
  };

  const reasons = dispositionReasonOptions('not_interested');
  const trying = OUTCOME_MENU.filter((m) => !m.finishes);
  const finishing = OUTCOME_MENU.filter((m) => m.finishes);

  const row = (m: (typeof OUTCOME_MENU)[number]) => {
    const n = OUTCOME_MENU.indexOf(m) + 1;
    return (
      <button
        key={m.value}
        type="button"
        role="menuitem"
        onClick={() => { void choose(m.value); }}
        disabled={!!busy}
        className={`${menuItemCls} ${m.value === 'asked_to_stop' ? 'text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20' : ''}`}
      >
        {busy === m.value
          ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-gray-400" aria-hidden="true" />
          : <span className="w-3.5 flex-shrink-0 text-center text-[10px] font-semibold tabular-nums text-gray-400 dark:text-gray-500" aria-hidden="true">{n}</span>}
        {m.label}
      </button>
    );
  };

  return (
    <div ref={rootRef} className={`relative inline-flex flex-shrink-0 ${fullOnPhone}`}>
      <div className={`inline-flex ${fullOnPhone}`} role="group" aria-label="Log what happened">
        <button
          type="button"
          onClick={() => { void log(primary); }}
          disabled={disabled || !!busy}
          title={`Log "${labelOf(primary)}" for this person`}
          className={`inline-flex items-center justify-center gap-1 bg-primary-600 font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 ${size.main}`}
        >
          {busy === primary
            ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
            : primary === 'no_answer' && <PhoneOff className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          <span className="truncate">{labelOf(primary)}</span>
        </button>
        <button
          type="button"
          onClick={() => (open ? close() : setOpen(true))}
          disabled={disabled || !!busy}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          title="Log a different outcome"
          className={`inline-flex items-center gap-1 border-l border-primary-500 bg-primary-600 font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-700 dark:focus:ring-offset-gray-800 ${size.more}`}
        >
          {/* Words where there is room; the bar is width-budgeted, so there
              (and on phones, Call Mode) the chevron carries it with a
              screen-reader label. The lead page's control is full-width on a
              phone, so its words fit at every size. */}
          <span className={surface === 'bar' ? 'sr-only' : surface === 'lead' ? '' : 'sr-only sm:not-sr-only'}>Log outcome</span>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Log outcome"
          onKeyDown={onMenuKey}
          className={`absolute ${edge} top-full mt-1.5 w-72 max-w-[calc(100vw-1.5rem)] ${Z.overlay} rounded-xl border border-gray-200 bg-white p-1.5 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
            // Under a full-width control the menu spans the control itself.
            surface === 'lead' ? 'max-sm:left-0 max-sm:w-auto' : ''
          }`}
        >
          {view === 'main' && (
            <>
              <div className="px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Tried them
              </div>
              {trying.map(row)}
              <div className="mx-2.5 my-1 border-t border-gray-100 dark:border-gray-700" />
              <div className="px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Reached — finishes them on the list
              </div>
              {finishing.map(row)}
            </>
          )}

          {view === 'not_interested' && (
            <>
              {backRow('Why not interested?')}
              {reasons.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  role="menuitem"
                  onClick={() => { void log('not_interested', { reason_code: r.value }); }}
                  disabled={!!busy}
                  className={menuItemCls}
                >
                  {r.label}
                </button>
              ))}
            </>
          )}

          {view === 'call_back' && timeStep('call_back')}
          {view === 'try_later' && timeStep('try_later')}
        </div>
      )}

      {flash && !open && (
        <div
          role={flash.tone === 'error' ? 'alert' : 'status'}
          className={`absolute ${edge} top-full mt-2 w-max max-w-[18rem] ${Z.overlay} flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs leading-snug shadow-md ${
            flash.tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300'
              : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
          }`}
        >
          {flash.tone === 'ok' && <Check className="mt-px h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />}
          <span>{flash.text}</span>
        </div>
      )}
    </div>
  );
}
